import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { ThemeProvider, createTheme, styled } from '@mui/material/styles';

const contactSelectTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#f87171' },
    text: { primary: '#f1f5f9', secondary: '#94a3b8' },
    background: { paper: 'rgba(18, 18, 28, 0.98)', default: '#07070c' },
    error: { main: '#fca5a5' },
  },
  shape: { borderRadius: 10 },
  typography: {
    fontFamily: '"Outfit", "Segoe UI", system-ui, -apple-system, sans-serif',
  },
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(7, 7, 12, 0.9)',
          '& fieldset': {
            borderColor: 'rgba(255, 255, 255, 0.12)',
          },
          '&:hover fieldset': {
            borderColor: 'rgba(248, 113, 113, 0.38)',
          },
          '&.Mui-focused fieldset': {
            borderColor: 'rgba(248, 113, 113, 0.65)',
            borderWidth: '1px',
          },
        },
        input: {
          padding: '10.5px 14px',
          fontSize: '0.92rem',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#94a3b8',
          fontSize: '0.82rem',
          fontWeight: 500,
          '&.Mui-focused': {
            color: '#fecaca',
          },
        },
        shrink: {
          fontSize: '0.82rem',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
          backgroundColor: 'rgba(18, 18, 28, 0.98)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 10,
          marginTop: 6,
          maxHeight: 320,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.92rem',
          lineHeight: 1.45,
          '&.Mui-selected': {
            backgroundColor: 'rgba(220, 38, 38, 0.22)',
          },
          '&.Mui-selected:hover': {
            backgroundColor: 'rgba(220, 38, 38, 0.3)',
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: '0.78rem',
          marginLeft: 2,
          marginTop: 6,
        },
      },
    },
  },
});

const StyledFormControl = styled(FormControl)({
  width: '100%',
  marginTop: 0,
});

const StyledSelect = styled(Select)({
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiSelect-icon': {
    color: 'rgba(248, 113, 113, 0.8)',
  },
});

const LABEL = 'Service you want from us';

/**
 * MUI Outlined Select styled for the contact form (dark + crimson accents).
 */
export default function ContactServiceSelect({ id, labelId, value, onChange, error, helperText, options }) {
  const selectable = options.filter((o) => o.value !== '');

  return (
    <ThemeProvider theme={contactSelectTheme}>
      <StyledFormControl fullWidth error={error} variant="outlined">
        <InputLabel id={labelId} htmlFor={id} shrink>
          {LABEL}
        </InputLabel>
        <StyledSelect
          labelId={labelId}
          id={id}
          name="service"
          value={value}
          label={LABEL}
          onChange={(e) => onChange(e.target.value)}
          displayEmpty
          notched
          MenuProps={{
            disableScrollLock: true,
            PaperProps: {
              elevation: 8,
            },
          }}
          renderValue={(selected) => {
            if (selected === '' || selected == null) {
              return <span style={{ opacity: 0.6 }}>Select what you need…</span>;
            }
            const o = options.find((x) => x.value === selected);
            return o?.label ?? selected;
          }}
        >
          <MenuItem value="" disabled>
            <em>Select what you need…</em>
          </MenuItem>
          {selectable.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </StyledSelect>
        {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
      </StyledFormControl>
    </ThemeProvider>
  );
}
